import type { KeyboardEvent } from 'react';

export const DEFAULT_WAITING_HOURS_FOR_DEPLOYMENT = 48;
export const MIN_WAITING_HOURS_FOR_DEPLOYMENT = 12;
export const MAX_WAITING_HOURS_FOR_DEPLOYMENT = 720;

const NON_INTEGER_DELAY_INPUT_KEYS: readonly string[] = [
  'e',
  'E',
  '+',
  '-',
  '.',
  ',',
];

export const DEPLOYMENT_DELAY_INPUT_MAX = MAX_WAITING_HOURS_FOR_DEPLOYMENT;

export const clampDeploymentDelayHours = (hours: number): number =>
  Math.min(
    Math.max(hours, MIN_WAITING_HOURS_FOR_DEPLOYMENT),
    MAX_WAITING_HOURS_FOR_DEPLOYMENT,
  );

export const formatDeploymentDelayDraft = (
  hours: number | null | undefined,
): string => (hours != null ? String(hours) : '');

export const applyDeploymentDelayInputChange = (raw: string): string => {
  if (raw === '') {
    return '';
  }

  if (!/^\d+$/.test(raw)) {
    return '';
  }

  const parsed = Number.parseInt(raw, 10);

  if (Number.isNaN(parsed)) {
    return '';
  }

  if (parsed > MAX_WAITING_HOURS_FOR_DEPLOYMENT) {
    return String(MAX_WAITING_HOURS_FOR_DEPLOYMENT);
  }

  return raw;
};

export const normalizeDeploymentDelayDraft = (draft: string): string => {
  if (draft.trim() === '') {
    return '';
  }

  const parsed = Number.parseInt(draft, 10);

  if (Number.isNaN(parsed)) {
    return '';
  }

  return String(clampDeploymentDelayHours(parsed));
};

export const parseDeploymentDelayDraft = (draft: string): number | null => {
  const trimmed = draft.trim();

  if (trimmed === '') {
    return null;
  }

  const parsed = Number.parseInt(trimmed, 10);

  if (Number.isNaN(parsed) || parsed < MIN_WAITING_HOURS_FOR_DEPLOYMENT) {
    return null;
  }

  return parsed;
};

export const isDeploymentDelayDraftSubmittable = (draft: string): boolean =>
  parseDeploymentDelayDraft(draft) !== null;

export const blockDeploymentDelayNonIntegerKeys = (
  event: KeyboardEvent<HTMLInputElement>,
): void => {
  if (NON_INTEGER_DELAY_INPUT_KEYS.includes(event.key)) {
    event.preventDefault();
  }
};

export const getCurrentDeploymentDelayHours = (
  waitingHoursForDeployment: number | null | undefined,
): number | undefined => waitingHoursForDeployment ?? undefined;
